
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useDarkMode } from "../../contexts/DarkModeContext";
import { FaArrowLeft, FaCheck, FaTimes, FaBook, FaClock } from "react-icons/fa";
import { toast } from "react-toastify";
import { fetchDutyDetails, clearDutyDetailsError, resetDutyDetails } from "../../redux/slices/dutyDetailsSlice";
import { startDutySession, submitDutyAnswers } from "../../redux/slices/dutySessionSlice";
import "./DutySolver.css";

function DutySolver() {
  const { isDarkMode } = useDarkMode();
  const { dutyId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // Get user role from Redux store
  const { currentUser } = useSelector((state) => state.auth);
  const userRole = currentUser?.role || localStorage.getItem("userRole");
  const isTeacher = userRole === "teacher";
  const isStudent = userRole === "student";
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // Get lessonId from URL state or localStorage
  const [lessonId, setLessonId] = useState(null);
  const { dutyData, loading: dutyLoading, error: dutyError } = useSelector((state) => state.dutyDetails);
  const [token, setToken] = useState(null);

  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [selectedAnswersMap, setSelectedAnswersMap] = useState({}); // في حال تعدد الأسئلة مستقبلاً
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(600); // 10 minutes default

  // Get lessonId and token from URL params or localStorage
  useEffect(() => {
    const storedToken = localStorage.getItem('token3almny');
    
    // Get lessonId from URL params first
    const urlParams = new URLSearchParams(window.location.search);
    const urlLessonId = urlParams.get('lessonId');
    
    if (urlLessonId) {
      setLessonId(urlLessonId);
    } else {
      // Fallback to localStorage if not in URL
      const storedLessonId = localStorage.getItem('currentLessonId');
      if (storedLessonId) {
        setLessonId(storedLessonId);
      }
    }
    
    if (storedToken) {
      setToken(storedToken);
    }
  }, []);

  // Fetch duty details when component mounts
  useEffect(() => {
    if (dutyId && lessonId) {
      console.log('Fetching duty details:', { lessonId, dutyId });
      dispatch(resetDutyDetails());
      dispatch(fetchDutyDetails({ lessonId, dutyId }));
    } else {
      console.log('Missing required data:', { dutyId, lessonId });
    }
  }, [dispatch, dutyId, lessonId]);

  // Start duty session for students (mirrors StartExam logic)
  useEffect(() => {
    if (isStudent && lessonId) {
      dispatch(startDutySession(lessonId));
    }
  }, [dispatch, isStudent, lessonId]);

  // Handle duty error
  useEffect(() => {
    if (dutyError) {
      toast.error(dutyError);
      dispatch(clearDutyDetailsError());
    }
  }, [dutyError, dispatch]);

  // Set initial time when duty data is loaded
  useEffect(() => {
    if (dutyData) {
      setTimeLeft(600); // 10 minutes default
    }
  }, [dutyData]);

  useEffect(() => {
    if (timeLeft > 0 && !isSubmitted) {
      const timer = setTimeout(() => {
        setTimeLeft(timeLeft - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && !isSubmitted) {
      handleSubmit();
    }
  }, [timeLeft, isSubmitted]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleAnswerSelect = (answerId) => {
    if (!isSubmitted) {
      setSelectedAnswer(answerId);
      // حفظ في الخريطة لاستخدام إرسال متعدد لاحقاً
      setSelectedAnswersMap({ 1: answerId });
    }
  };

  const handleSubmit = async () => {
    if (!dutyData || !dutyData.answers || dutyData.answers.length === 0) {
      toast.error("لا توجد أسئلة متاحة");
      return;
    }

    if (!selectedAnswer) {
      toast.warning("يرجى اختيار إجابة");
      return;
    }

    // إرسال للإندبوينت: duties/submit_duty/{dutyId}
    // Backend expects: { answers: [{ question_id, answer_id }] }
    const payloadAnswers = [
      {
        question_id: dutyData.id, // إذا توفر question_id مختلف عدّله هنا
        answer_id: selectedAnswer,
      },
    ];

    const res = await dispatch(submitDutyAnswers({ lessonId, answers: payloadAnswers }));
    if (res.meta.requestStatus === 'fulfilled') {
      setIsSubmitted(true);
      const responseData = res.payload;
      
      // عرض رسالة النجاح من الـ API أو رسالة افتراضية
      const successMessage = responseData?.message || 'تم إرسال الإجابة بنجاح';
      toast.success(`✅ ${successMessage}`);
      
      // العودة لصفحة تفاصيل الحصة بعد ثانيتين
      setTimeout(() => {
        navigate(`/lessonDetails/${lessonId}`);
      }, 2000);
    } else {
      toast.error(res.payload || '❌ فشل إرسال الإجابة');
      navigate(`/lessonDetails/${lessonId}`);

    }
  };

  const handleRetry = () => {
    setSelectedAnswer(null);
    setIsSubmitted(false);
    setScore(0);
    setTimeLeft(600); // 10 minutes default
  };

  // Prevent teachers from accessing duty solver
  if (isTeacher) {
    return (
      <div className={`duty-solver-page ${isDarkMode ? "dark-mode" : "light-mode"}`}>
        <div className="container">
          <div className="text-center py-5">
            <h3>غير مصرح للمعلمين</h3>
            <p>هذه الصفحة مخصصة للطلاب فقط</p>
            <button className="btn btn-primary mt-3" onClick={() => navigate(-1)}>
              العودة للصفحة السابقة
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show loading state
  if (dutyLoading) {
    return (
      <div className={`duty-solver-page ${isDarkMode ? "dark-mode" : "light-mode"}`}>
        <div className="container">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">جاري التحميل...</span>
            </div>
            <p className="mt-3">جاري تحميل الواجب...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state if no token
  if (!token) {
    return (
      <div className={`duty-solver-page ${isDarkMode ? "dark-mode" : "light-mode"}`}>
        <div className="container">
          <div className="text-center py-5">
            <h3>غير مصرح لك بالوصول</h3>
            <p>يرجى تسجيل الدخول أولاً</p>
            <button className="btn btn-primary mt-3" onClick={() => navigate('/login')}>
              تسجيل الدخول
            </button>
            <button className="btn btn-secondary mt-2 ms-2" onClick={() => navigate(`/lessonDetails/${lessonId}`)}>
              العودة للصفحة السابقة
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show error state if no lessonId
  if (!lessonId) {
    return (
      <div className={`duty-solver-page ${isDarkMode ? "dark-mode" : "light-mode"}`}>
        <div className="container">
          <div className="text-center py-5">
            <h3>خطأ في تحميل الواجب</h3>
            <p>لم يتم العثور على معرف الحصة</p>
            <div className="debug-info mt-3 p-3 bg-light rounded">
              <small className="text-muted">
                <strong>معلومات التصحيح:</strong><br/>
                dutyId: {dutyId}<br/>
                lessonId: {lessonId}<br/>
                URL params: {window.location.search}
              </small>
            </div>
            <button className="btn btn-primary mt-3" onClick={() => navigate(`/lessonDetails/${lessonId}`)}>
              العودة للصفحة السابقة
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (!dutyData) {
    return (
      <div className={`duty-solver-page ${isDarkMode ? "dark-mode" : "light-mode"}`}>
        <div className="container">
          <div className="text-center py-5">
            <h3>لا يمكن تحميل الواجب</h3>
            <p>جاري محاولة تحميل الواجب...</p>
            <button className="btn btn-primary mt-3" onClick={() => navigate(`/lessonDetails/${lessonId}`)}>
              العودة للصفحة السابقة
            </button>
          </div>
        </div>
      </div>
    );
  }

  
  return (
    <div className={`duty-solver-page ${isDarkMode ? "dark-mode" : "light-mode"}`}>
      <div className="container">
        {/* Header */}
        <div className="duty-solver-header">
          <button 
            className="back-btn"
            onClick={() => navigate(`/lessonDetails/${lessonId}`)}
          >
            <FaArrowLeft />
            رجوع
          </button>
          
          <div className="duty-solver-title">
            <h1>{dutyData.title || "واجب"}</h1>
            <p>{dutyData.description || "حل السؤال التالي"}</p>
          </div>

          <div className="duty-solver-timer">
            <FaClock />
            <span>{formatTime(timeLeft)}</span>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="duty-solver-progress">
          <div className="progress-bar">
            <div 
              className="progress-fill"
              style={{ width: `${isSubmitted ? 100 : 50}%` }}
            ></div>
          </div>
          <span className="progress-text">
            {isSubmitted ? "تم الإرسال" : "السؤال 1 من 1"}
          </span>
        </div>

        {/* Question Content */}
        <div className="duty-solver-content">
          {!isSubmitted ? (
            dutyData && dutyData.answers && dutyData.answers.length > 0 ? (
              <div className="question-container">
                <div className="question-header">
                  <FaBook size={20} />
                </div>
              
                <div className="question-text">
                  <div className="question-number">
                    السؤال 1
                  </div>
                  <p>{dutyData.title}</p>
                </div>

                <div className="answers-container">
                  {dutyData.answers.map((answer, answerIndex) => (
                    <button
                      key={answer.id}
                      className={`answer-btn ${
                        selectedAnswer === answer.id ? 'selected' : ''
                      }`}
                      onClick={() => handleAnswerSelect(answer.id)}
                    >
                      <span className="answer-letter">
                        {String.fromCharCode(65 + answerIndex)}
                      </span>
                      <span className="answer-text">{answer.answer}</span>
                    </button>
                  ))}
                </div>

                <div className="question-actions">
                  <button
                    className="btn btn-success"
                    onClick={handleSubmit}
                    disabled={!selectedAnswer}
                  >
                    إرسال الإجابة
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center py-5">
                <p>لا توجد إجابات متاحة</p>
                <div className="debug-info mt-3 p-3 bg-light rounded">
                  <small className="text-muted">
                    <strong>معلومات التصحيح:</strong><br/>
                    dutyData: {dutyData ? 'loaded' : 'null'}<br/>
                    dutyData.answers: {dutyData?.answers ? dutyData.answers.length : 'undefined'}<br/>
                    dutyData.title: {dutyData?.title || 'undefined'}
                  </small>
                </div>
              </div>
            )
          ) : (
            <div className="results-container">
              <div className="results-header">
                <h2>نتيجة الواجب</h2>
                <div className="score-display">
                  <span className="score-percentage">{score}%</span>
                  <span className="score-label">النتيجة</span>
                </div>
              </div>

              <div className="results-details">
                <div className="result-item">
                  <span>الإجابة المختارة:</span>
                  <span>{selectedAnswer ? "نعم" : "لا"}</span>
                </div>
                <div className="result-item">
                  <span>الوقت المستغرق:</span>
                  <span>{formatTime(600 - timeLeft)}</span>
                </div>
              </div>

              <div className="results-actions">
                <button className="btn btn-primary" onClick={handleRetry}>
                  إعادة المحاولة
                </button>
                <button className="btn btn-secondary" onClick={() => navigate(-1)}>
                  العودة للواجبات
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DutySolver; 
